import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export type QuickFilter = "all" | "for_you" | "popular" | "top_rated" | "new_releases"

type DefaultState = {
  period: number[]
  genres: number[]
  search: string
  periodCHanged: boolean
  openModal: boolean
  mutedState: boolean
  muteInteracted: boolean
  filterChanged: boolean
  temporaryCounter: number
  maxYear: number
  tempGenres: number[]
  tempPeriod: number[]
  quickFilter: QuickFilter
}

const MUTED_STORAGE_KEY = "hiviewerMutedState"

const getSavedMutedState = () => {
  if (typeof window === "undefined") return false

  try {
    return window.localStorage.getItem(MUTED_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

const getSavedMuteInteracted = () => {
  if (typeof window === "undefined") return false

  try {
    return window.localStorage.getItem(MUTED_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

const saveMutedState = (value: boolean) => {
  if (typeof window === "undefined") return

  try {
    window.localStorage.setItem(MUTED_STORAGE_KEY, String(value))
  } catch {
    // localStorage can be unavailable in private or restricted browser modes.
  }
}

const initialState: DefaultState = {
  period: [1980, 2025],
  genres: [],
  search: "",
  periodCHanged: false,
  openModal: false,
  mutedState: getSavedMutedState(),
  muteInteracted: getSavedMuteInteracted(),
  filterChanged: false,
  temporaryCounter: 0,
  tempGenres: [],
  maxYear: 2025,
  tempPeriod: [1980, 2025],
  quickFilter: "all" as QuickFilter,
}

export const FiltersSlice = createSlice({
  name: "filters",
  initialState: initialState,
  reducers: {
    setOpenModal: (state, action: PayloadAction<boolean>) => {
      state.openModal = action.payload
    },
    setPeriod: (state, action: PayloadAction<number[]>) => {
      state.periodCHanged = action.payload[0] !== 1980 || action.payload[1] !== state.maxYear

      state.period = [
        action.payload[0] < 1980 ? 1980 : action.payload[0],
        action.payload[1] > state.maxYear ? state.maxYear : action.payload[1],
      ]
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload
    },
    setGenres: (state, action: PayloadAction<number[]>) => {
      state.genres = action.payload
    },
    setMutedState: (state, action: PayloadAction<boolean>) => {
      state.mutedState = action.payload;
      state.muteInteracted = true;
      saveMutedState(action.payload);
    },
    setMaxYear: (state, action: PayloadAction<number>) => {
      state.maxYear = action.payload;
      state.period = [1980, action.payload]
      state.tempPeriod = [1980, action.payload]
    },
    setFilterChanged: (state, action: PayloadAction<boolean>) => {
      state.filterChanged = action.payload
    },// @ts-ignore
    setTemporaryCounter: (state, action: PayloadAction<any>) => {
      let counter = 0;
      if (action.payload.period[0] !== 1980 ||  action.payload.period[1] !== state.maxYear) {
        counter += 1;
      };

      counter += action.payload.genres.length;

      state.temporaryCounter =  counter;
    },// @ts-ignore
    setTemporaryGenres: (state, action: PayloadAction<any>) => {
      if (state.tempGenres.includes(action.payload)) {
        state.tempGenres =  state.tempGenres.filter((currentId: any) => currentId !== action.payload);
      } else {
        state.tempGenres =  [...state.tempGenres, action.payload];
      }
    },
    setTemporaryPeriod: (state, action: PayloadAction<number[]>) => {
      state.tempPeriod = [
        action.payload[0] < 1980 ? 1980 : action.payload[0],
        action.payload[1] > state.maxYear ? state.maxYear : action.payload[1],
      ]
    },
    resetFilters: (state) => {
      state.tempGenres = []
      state.genres = []
      state.tempPeriod = [1980, state.maxYear]
      state.period = [1980, state.maxYear]
    },
    resetPeriod: (state) => {
      state.tempPeriod = [1980, state.maxYear]
      state.period = [1980, state.maxYear]
    },
    resetGenres: (state) => {
      state.tempGenres = []
      state.genres = []
    },
    setQuickFilter: (state, action: PayloadAction<QuickFilter>) => {
      state.quickFilter = action.payload
    }
  },
})

export default FiltersSlice.reducer
export const {
  resetGenres,
  resetPeriod,
  setPeriod,
  setMaxYear,
  setGenres,
  setSearch,
  setOpenModal ,
  setMutedState,
  setFilterChanged,
  setTemporaryCounter,
  setTemporaryGenres,
  setTemporaryPeriod,
  resetFilters,
  setQuickFilter
} = FiltersSlice.actions
